import { ImageServiceType } from '@omni/kit/Types';
import KitImage from '@omni/kit/components/KitImage';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import { parseImageUrl } from '@omni/kit/utilities/utilities';
import React, { ReactElement } from 'react';
import { useMemo } from 'react';
import { useCallback, useState } from 'react';
import { LayoutChangeEvent } from 'react-native';
import { StyleSheet, View, ViewStyle } from 'react-native';

const debug = require('debug')('omni:kit:components:KitImageContainer');

const LOGO_SIZE = 34;
const RESOLUTION_WIDTH_DEFAULT = 1920;
const RESOLUTION_HEIGHT_DEFAULT = 1080;
const LOGO_RATIO = 40 / 100; /** 40% */

export interface KitImageContainerProps {
  backgroundColor?: string;
  borderRadius?: number;
  imageUrl?: string;
  logoBackgroundColor?: string;
  logoUrl?: string;
  style?: ViewStyle | ViewStyle[];
}

/**
 * Provide default image container with default branding color, logo and image.
 */
export default ({
  backgroundColor = '',
  borderRadius = BorderRadius.m,
  imageUrl = '',
  logoBackgroundColor = '',
  logoUrl = '',
  style = [],
}: KitImageContainerProps): ReactElement => {
  if (!Array.isArray(style)) style = [style];

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { width, height } = event.nativeEvent.layout;

      if (!dimensions.width || !dimensions.height) {
        setDimensions({ width, height });
      }
    },
    [dimensions]
  );

  const logoSize = useMemo(() => {
    return (
      Math.min(dimensions.width, dimensions.height) * LOGO_RATIO || LOGO_SIZE
    );
  }, [dimensions]);

  const parsedLogourl = useMemo(() => {
    const parsedUrl =
      parseImageUrl(logoUrl, logoSize, logoSize, ImageServiceType.ImagePng) ||
      '';

    try {
      const urlObj = new URL(parsedUrl);

      urlObj.searchParams.set('f', 'white');

      return urlObj.toString();
    } catch (e) {
      debug('Failed to apply `f=white` query param', parsedUrl);

      return parsedUrl;
    }
  }, [logoSize, logoUrl]);

  const parsedImageUrl = useMemo(() => {
    return parseImageUrl(
      imageUrl,
      dimensions.width || RESOLUTION_WIDTH_DEFAULT,
      dimensions.height || RESOLUTION_HEIGHT_DEFAULT,
      ImageServiceType.ImageJpeg
    );
  }, [dimensions, imageUrl]);

  return (
    <View
      style={[
        {
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          borderRadius: borderRadius,
        },
        ...style,
      ]}
      onLayout={onLayout}
    >
      <View
        style={[
          styles.image,
          {
            backgroundColor:
              backgroundColor || logoBackgroundColor || Colors.N1000,
            borderRadius,
          },
        ]}
      >
        {parsedLogourl ? (
          <KitImage
            source={{
              uri: parsedLogourl,
              width: logoSize,
              height: logoSize,
            }}
            style={{
              borderRadius: 3,
              opacity: 0.3,
            }}
            resizeMode='cover'
          />
        ) : null}
      </View>

      {parsedImageUrl ? (
        <KitImage
          source={{
            uri: parsedImageUrl,
          }}
          style={[styles.image, { borderRadius }]}
          resizeMode='cover'
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  image: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    overflow: 'hidden',
    position: 'absolute',
    right: 0,
    top: 0,
  },
});
